/**
 * renderAssetStore.ts
 * Module-level singleton for the active {@link RenderAssets} bundle.
 *
 * Both the React Canvas component ({@link GameCanvas}) and the vanilla DOM
 * game host (Vite build) read from this store every frame so the loop body
 * can stay framework-agnostic. Set once by the screen after all
 * HTMLImageElements have loaded.
 *
 * @module renderAssetStore
 */

import type { WorldId } from '../../constants/gameConstants';
import type { RenderAssets } from './RenderAssets';
import type { TiledMap } from './TileMapTypes';

let _assets: RenderAssets | null = null;

// DECISION: Cache a pending tile-map switch when assets haven't loaded
// yet, so the first setRenderAssets call applies the newest map instead
// of the one captured at mount time.
let _pendingTiledMap: TiledMap | null = null;

// DECISION: Track the active world id alongside the tile map so the
// renderer can pick per-world parallax art without re-deriving the world
// from the tile map every frame. Defaults to 1 since W1 was the only
// world before W3 parallax art landed.
let _activeWorldId: WorldId = 1;

/**
 * Replace the active asset bundle. If a tile map was queued via
 * {@link setActiveTiledMap} while assets were still loading, that map
 * overrides whatever was baked into `assets.tiledMap`.
 *
 * @param assets Fully loaded RenderAssets bundle.
 */
export function setRenderAssets(assets: RenderAssets): void {
  if (_pendingTiledMap !== null) {
    assets.tiledMap = _pendingTiledMap;
    _pendingTiledMap = null;
  }
  _assets = assets;
}

/**
 * Swap the active tiled map without rebuilding the asset bundle.
 *
 * @param tiledMap The Tiled JSON for the level to render now.
 */
export function setActiveTiledMap(tiledMap: TiledMap): void {
  if (_assets !== null) {
    _assets.tiledMap = tiledMap;
    return;
  }
  _pendingTiledMap = tiledMap;
}

/** Current asset bundle, or null if none has been loaded yet. */
export function getRenderAssets(): RenderAssets | null {
  return _assets;
}

/**
 * Record which world the active level belongs to. Call alongside
 * {@link setActiveTiledMap} so per-world rendering (parallax art, etc.)
 * stays in sync with the level being drawn.
 */
export function setActiveWorldId(worldId: WorldId): void {
  _activeWorldId = worldId;
}

/** Currently active world id (defaults to 1). */
export function getActiveWorldId(): WorldId {
  return _activeWorldId;
}

/** Test-only — clear all registered assets. */
export function _resetRenderAssetsForTesting(): void {
  _assets = null;
  _pendingTiledMap = null;
  _activeWorldId = 1;
}
